import React, { Fragment } from 'react';
import { Check, Minus } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
type CellValue = boolean | string;
type Plan = {
  id: 'base' | 'middle' | 'enterprise';
  name: string;
  price: string;
  priceLabel: string;
  highlighted?: boolean;
};
type Feature = {
  label: string;
  hint?: string;
  values: [CellValue, CellValue, CellValue];
};
const plans: Plan[] = [{
  id: 'base',
  name: 'Base',
  price: '€ 99',
  priceLabel: 'a partire da'
}, {
  id: 'middle',
  name: 'Middle',
  price: '€ 399',
  priceLabel: 'a partire da',
  highlighted: true
}, {
  id: 'enterprise',
  name: 'Enterprise',
  price: '€ 879',
  priceLabel: 'a partire da'
}];
const features: Feature[] = [{
  label: 'Tutti i corrieri gestiti inclusi',
  values: [true, true, true]
}, {
  label: 'Dashboard e gestione tracking ordini',
  values: [true, true, true]
}, {
  label: 'Dashboard e gestione tracking resi',
  values: [true, true, true]
}, {
  label: 'Accesso API',
  values: [false, true, true]
}, {
  label: 'Tracking Page',
  values: [false, true, true]
}, {
  label: 'Svincolo giacenze',
  values: [false, true, true]
}, {
  label: 'Chatbot AI Sito Web',
  values: [false, true, true]
}, {
  label: 'Dinamic Tracking',
  values: [false, true, true]
}, {
  label: 'Callcenter Dedicato',
  hint: '**',
  values: ['a partire da € 0,40 / ticket', 'a partire da € 0,30 / ticket', 'a partire da € 0,20 / ticket']
}, {
  label: 'AI Prediction Delivery',
  values: [false, false, true]
}, {
  label: 'Gestione intelligente dei listini',
  values: [false, false, true]
}, {
  label: 'Raccolta autonoma recensioni',
  values: [false, false, true]
}, {
  label: 'Flussi gestione avanzata',
  values: ['Su richiesta', 'Su richiesta', 'Su richiesta']
}, {
  label: 'Costo Label',
  hint: '*',
  values: ['€ 0,14*', '€ 0,10*', '€ 0,07*']
}];
function CellContent({
  value
}: {
  value: CellValue;
}) {
  if (value === true) {
    return <div className="flex items-center justify-center" data-id="element-237">
        <Check className="w-5 h-5 color-accent" strokeWidth={2.5} aria-label="Incluso" data-id="element-238" />
      </div>;
  }
  if (value === false) {
    return <div className="flex items-center justify-center" data-id="element-239">
        <Minus className="w-4 h-4 color-ink-tertiary" aria-label="Non incluso" data-id="element-240" />
      </div>;
  }
  return <div className="text-body-sm color-ink-secondary text-center" data-id="element-241">{value}</div>;
}
export function Pricing() {
  return <section className="section-pad border-t border-default" id="pricing" data-id="element-242">
      <div className="wrap" data-id="element-243">
        {/* Header */}
        <AnimatedSection delay={0} data-id="element-244">
          <div className="max-w-[64ch] mb-12 md:mb-16" data-id="element-245">
            <div className="section-tag section-tag--accent mb-6" data-id="element-246">PRICING</div>
            <h2 className="text-display-section mb-6" data-id="element-247">
              Un piano per ogni fase. Paghi solo quello che ti serve.
            </h2>
            <p className="text-body-lg color-ink-secondary" data-id="element-248">
              Aisac è modulare: scegli il piano che si adatta meglio al tuo
              eCommerce e scala quando i volumi crescono. Niente fee nascoste,
              tariffe per label e ticket trasparenti.
            </p>
          </div>
        </AnimatedSection>

        {/* Comparison table — desktop */}
        <AnimatedSection delay={0.1} data-id="element-249">
          <div className="hidden md:block rounded-lg border border-default bg-[#0a0a0a] overflow-hidden" data-id="element-250">
            <div className="grid grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))]" data-id="element-251">
              {/* Header row */}
              <div className="px-6 py-5 border-b border-default" data-id="element-252">
                <div className="text-mono-label color-ink-tertiary uppercase tracking-wider" data-id="element-253">
                  Confronta i piani
                </div>
              </div>
              {plans.map(plan => <div key={plan.id} className={`px-6 py-5 border-b border-default text-center ${plan.highlighted ? 'bg-elev1' : ''}`} data-id="element-254">
                  <div className="color-ink-primary mb-1" style={{
                fontFamily: 'var(--font-display)',
                fontSize: '20px',
                fontWeight: 500,
                letterSpacing: '-0.02em'
              }} data-id="element-255">
                    {plan.name}
                  </div>
                  <div className="text-body-sm color-accent" data-id="element-256">
                    {plan.priceLabel} {plan.price} / mese
                  </div>
                </div>)}

              {/* Feature rows */}
              {features.map((feature, idx) => <Fragment key={feature.label} data-id="element-257">
                  <div className={`px-6 py-4 text-body-base color-ink-primary ${idx < features.length - 1 ? 'border-b border-default' : ''}`} data-id="element-258">
                    {feature.label}
                    {feature.hint && <span className="color-ink-tertiary ml-1" data-id="element-259">
                        {feature.hint}
                      </span>}
                  </div>
                  {feature.values.map((value, vIdx) => <div key={vIdx} className={`px-6 py-4 flex items-center justify-center ${idx < features.length - 1 ? 'border-b border-default' : ''} ${plans[vIdx].highlighted ? 'bg-elev1' : ''}`} data-id="element-260">
                      <CellContent value={value} data-id="element-261" />
                    </div>)}
                </Fragment>)}
            </div>
          </div>
        </AnimatedSection>

        {/* Comparison — mobile (stacked per plan) */}
        <div className="md:hidden flex flex-col gap-4" data-id="element-262">
          {plans.map((plan, pIdx) => <AnimatedSection key={plan.id} delay={0.05 + pIdx * 0.05} data-id="element-263">
              <div className={`rounded-lg border bg-[#0a0a0a] overflow-hidden ${plan.highlighted ? 'border-[var(--accent-500)]' : 'border-default'}`} data-id="element-264">
                <div className="px-5 py-4 border-b border-default" data-id="element-265">
                  <div className="color-ink-primary" style={{
                fontFamily: 'var(--font-display)',
                fontSize: '22px',
                fontWeight: 500,
                letterSpacing: '-0.02em'
              }} data-id="element-266">
                    {plan.name}
                  </div>
                  <div className="text-body-sm color-accent mt-1" data-id="element-267">
                    {plan.priceLabel} {plan.price} / mese
                  </div>
                </div>

                <ul className="flex flex-col" data-id="element-268">
                  {features.map((feature, fIdx) => {
                const value = feature.values[pIdx];
                return <li key={feature.label} className={`flex items-center justify-between gap-4 px-5 py-3 ${fIdx < features.length - 1 ? 'border-b border-default' : ''}`} data-id="element-269">
                        <span className="text-body-sm color-ink-primary flex-1" data-id="element-270">
                          {feature.label}
                          {feature.hint && <span className="color-ink-tertiary ml-1" data-id="element-271">
                              {feature.hint}
                            </span>}
                        </span>
                        <span className="shrink-0" data-id="element-272">
                          {typeof value === 'boolean' ? value ? <Check className="w-5 h-5 color-accent" strokeWidth={2.5} aria-label="Incluso" data-id="element-273" /> : <Minus className="w-4 h-4 color-ink-tertiary" aria-label="Non incluso" data-id="element-274" /> : <span className="text-body-sm color-ink-secondary text-right" data-id="element-275">
                              {value}
                            </span>}
                        </span>
                      </li>;
              })}
                </ul>
              </div>
            </AnimatedSection>)}
        </div>

        {/* Footnotes */}
        <AnimatedSection delay={0.15} data-id="element-276">
          <div className="mt-8 md:mt-10 space-y-2" data-id="element-277">
            <p className="text-body-sm color-ink-tertiary" data-id="element-278">
              <span className="color-ink-secondary" data-id="element-279">*</span> I prezzi sono da
              ritenersi soggetti a variazioni in base ai volumi gestiti. Possono
              subire importanti riduzioni.
            </p>
            <p className="text-body-sm color-ink-tertiary" data-id="element-280">
              <span className="color-ink-secondary" data-id="element-281">**</span> Possibilità di
              gestione di ticket complessi: Conferma, Upsell, Svincolo,
              Rimozione recensioni negative.
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>;
}